import Badge from 'atomic/badge';
import Link from 'atomic/link';
import ProductBoxPrice from 'modules/shared/components/product/components/product-box-price';
import Rating from 'atomic/rating';
import ProductListImpression from 'components/impressions/product-impression/list/product-list-impression';
import React from 'react';
import { ProductBoxLifestyleProps } from '../product-box-lifestyle.props';
import AlertDiscountPriceInfo from 'modules/shared/components/alert-discount-price-info';
import ProductBoxName from 'modules/shared/components/product/components/product-box-name';
import { prepareOfferDataForDiscount } from 'modules/shared/helpers/prepare-offer-data-for-discount';
import { SeoLinkRelEnum } from 'modules/seo-module/interfaces/seo-link-rel.enum';
import { LinkTarget } from 'atomic/link/link-target.enum';
import OfferLabel from 'modules/shared/modules/product/components/offer-label';
import { getMonoOfferLabel } from 'helpers/offer-label';

import './product-box-lifestyle.desktop.scss';

const ProductBoxLifestyleDesktop: React.FC<{
    product: ProductBoxLifestyleProps;
    mainImageLinkingToShop?: boolean;
}> = ({ product, mainImageLinkingToShop }) => {
    const shouldImageRedirectToOffer = product.offers.count === 1
        || (product.offers.count >= 1 && mainImageLinkingToShop && product.offers?.items[0]?.url);
    const {
        discount,
        minPrice30Days,
        price,
        offersCount,
        prevPrice,
        showDiscount,
        productHasNoOffers,
        dealerName
    } = prepareOfferDataForDiscount(
        product.minPrice,
        product.prevPrice,
        product.offers.items,
        product.offers.count
    );

    const offerLabel = getMonoOfferLabel(product);

    return (
        <ProductListImpression
            trackedData={{ ...product, category: { name: product.name }, price }}
            className="product-box-lifestyle-desktop-container"
        >
            <a
                className="product-box-lifestyle-desktop-link"
                href={shouldImageRedirectToOffer ? product.offers.items[0]?.url : product.url}
                rel={shouldImageRedirectToOffer || offersCount <= 1 ? SeoLinkRelEnum.NOOPENER_NOREFERRER : void 0}
                target={shouldImageRedirectToOffer ? LinkTarget.BLANK : void 0}
                aria-label={product.name}
            >
                {''}
            </a>
            <div className="product-box-lifestyle-desktop">
                {offerLabel && (
                    <div className="product-box-lifestyle-desktop-label">
                        <OfferLabel label={offerLabel} />
                    </div>
                )}
                <img className="product-box-lifestyle-desktop-image"
                    src={product.photos.main?.large}
                    alt={`${product.category.name} - ${product.name} - grafika 1`}
                    loading="lazy"
                />
                <div className="product-box-lifestyle-desktop__content">
                    <a
                        href={product.url}
                        data-ga="go-to-product-page"
                        rel={productHasNoOffers ? SeoLinkRelEnum.NOFOLLOW : void 0}
                        className="product-box-lifestyle-desktop__title"
                    >
                        <h2 title={product.name}>
                            {product.name}
                        </h2>
                    </a>
                    <Rating className="product-box-lifestyle-desktop__rating" opinions={product.opinions} />
                    <div className="product-box-lifestyle-desktop__price">
                        <ProductBoxPrice
                            price={price}
                            prevPrice={prevPrice}
                            offersCount={offersCount}
                            discount={discount}
                        />
                    </div>
                    {showDiscount && (
                        <div className="product-box-lifestyle-desktop__discount-info">
                            <AlertDiscountPriceInfo paddingTop={5} minPrice30Days={minPrice30Days} />
                        </div>
                    )}
                    <div className="product-box-lifestyle-desktop__dealer">
                        <ProductBoxName
                            dealerName={dealerName}
                            productHasNoOffers={productHasNoOffers}
                            className="product-box-lifestyle-desktop__dealer-name"
                        />
                        {showDiscount && (
                            <Badge className="product-box-lifestyle-desktop-discount" color="red">
                                <span>{discount}%</span>
                            </Badge>
                        )}
                    </div>
                    {offersCount <= 1
                        ? (
                            <Link
                                href={offersCount === 1 ? product.offers?.items[0]?.url : product.url}
                                data-ga="go-to-shop go-to-shop--d"
                                data-cy="go-to-shop"
                                size="md"
                                color={product.offers.items[0]?.url ? 'primary' : 'transparent'}
                                type="button"
                                rel={offersCount === 1 ? SeoLinkRelEnum.NOFOLLOW_NOOPENER_NOREFERRER : SeoLinkRelEnum.NOFOLLOW}
                                className="product-box-lifestyle-desktop__button"
                                target={offersCount === 1 ? LinkTarget.BLANK : void 0}
                            >
                                {product.offers?.items[0]?.url && offersCount === 1 ? 'Idź do sklepu' : 'Zobacz produkt'}
                            </Link>
                        )
                        : (
                            <Link
                                href={product.url}
                                data-ga="go-to-product-page go-to-product-page--d"
                                data-cy="go-to-product-page"
                                size="md"
                                color="transparent"
                                type="button"
                                rel={SeoLinkRelEnum.NOFOLLOW}
                                className="product-box-lifestyle-desktop__button"
                            >
                                Porównaj oferty ({offersCount})
                            </Link>
                        )
                    }
                </div>
            </div>
        </ProductListImpression>
    );
};

export default ProductBoxLifestyleDesktop;
